import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import Lightbox from '../components/Lightbox'

export default function GalleryPage() {
  const [pageData, setPageData] = useState(null)
  const [galleries, setGalleries] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeFilter, setActiveFilter] = useState('alle')
  const [lightboxState, setLightboxState] = useState({ isOpen: false, images: [], index: 0 })

  useEffect(() => {
    const fetchPage = fetch('https://api.dev.auxo.dk/public/content/pages')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const matchedPage = data.find((p) => p.page_slug === 'galleri')
          if (matchedPage) {
            setPageData(matchedPage)
          }
        }
      })

    const fetchGalleries = fetch('https://api.dev.auxo.dk/public/content/galleries')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setGalleries(data)
        }
      })

    Promise.all([fetchPage, fetchGalleries])
      .catch((err) => console.error('Failed to fetch gallery data', err))
      .finally(() => setLoading(false))
  }, [])

  const title = pageData?.meta_title || 'Galleri | Tømrerfirmaet Hansen og Mortensen Fredensborg'
  const description = pageData?.meta_description || 'Se billeder af vores professionelle tømrerarbejde, herunder tagarbejde, renovering, tilbygning, nybyg, gulvlægning samt vinduer og døre.'

  const filters = [
    { label: 'Alle', value: 'alle' },
    { label: 'Tagarbejde', value: 'tagarbejde' },
    { label: 'Renovering', value: 'renovering' },
    { label: 'Tilbygning', value: 'tilbygning' },
    { label: 'Nybyg', value: 'nybyg' },
    { label: 'Nyt gulv', value: 'nyt-gulv' },
    { label: 'Vinduer og døre', value: 'vinduer-og-doere' },
    { label: 'Hegn', value: 'hegn' },
    { label: 'Terrasser', value: 'terrasser' },
    { label: 'Loft', value: 'loft' },
    { label: 'Diverse', value: 'diverse' },
  ]

  // Flatten all images with their respective category slug for filtering
  const allImages = galleries.flatMap((gallery) =>
    (gallery.images || []).map((imgUrl) => ({
      url: imgUrl,
      category: gallery.gallery_slug,
    }))
  )

  const filteredImages = activeFilter === 'alle'
    ? allImages
    : allImages.filter((img) => img.category === activeFilter)

  const openLightbox = (index) => {
    const imagesList = filteredImages.map((img) => img.url)
    setLightboxState({
      isOpen: true,
      images: imagesList,
      index: index,
    })
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/37f7f16d-7c9c-4f81-b10c-2a08ddb4d637/imgi-4-trappe-i-laerk-100.png" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      {/* Hero Banner */}
      <section className="relative h-[40vh] min-h-[300px] w-full bg-neutral-950 flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/37f7f16d-7c9c-4f81-b10c-2a08ddb4d637/imgi-4-trappe-i-laerk-100.png"
            alt="Galleri"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 to-neutral-950/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <span className="text-red-500 text-xs font-extrabold uppercase tracking-widest mb-2 block">
            Referencer
          </span>
          <h1 className="text-3xl sm:text-5xl font-black text-white uppercase tracking-tight">
            Galleri
          </h1>
          <div className="flex items-center gap-2 text-xs text-zinc-400 uppercase tracking-wider mt-4">
            <Link to="/" className="hover:text-white transition-colors">Forside</Link>
            <span className="text-zinc-600">/</span>
            <span className="text-white">Galleri</span>
          </div>
        </div>
      </section>

      {/* Main Content & Filter Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Intro Text */}
          <div className="max-w-3xl mb-12">
            <h2 className="text-2xl sm:text-3xl font-black text-neutral-950 uppercase tracking-tight mb-4">
              Vores seneste projekter og referencer
            </h2>
            <p className="text-zinc-600 text-sm sm:text-base leading-relaxed">
              Her kan du se et udvalg af vores seneste projekter og referencer. Vi udfører en bred vifte af opgaver inden for tømrerfaget, og vi sætter en stor ære i at levere et solidt og professionelt resultat hver gang.
            </p>
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap gap-2 mb-12 border-b border-zinc-200 pb-6">
            {filters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setActiveFilter(filter.value)}
                className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-all duration-200 cursor-pointer ${
                  activeFilter === filter.value
                    ? 'bg-red-700 text-white'
                    : 'bg-zinc-100 text-zinc-800 hover:bg-zinc-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Images Grid */}
          {loading ? (
            <div className="min-h-[300px] flex items-center justify-center">
              <i className="fa-solid fa-spinner animate-spin text-red-700 text-4xl" />
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="text-center py-12 text-zinc-500 text-sm">
              Ingen billeder fundet i denne kategori.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredImages.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => openLightbox(idx)}
                  className="relative h-64 overflow-hidden border border-zinc-200 group cursor-pointer"
                >
                  <img
                    src={img.url.includes('assets.auxodev.dk') ? img.url.replace('assets.auxodev.dk/assets', 'assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets') : img.url}
                    alt={`Galleri ${idx + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-neutral-950/0 group-hover:bg-neutral-950/40 transition-all duration-300 flex items-center justify-center">
                    <i className="fa-solid fa-magnifying-glass-plus text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxState.isOpen && (
        <Lightbox
          images={lightboxState.images}
          initialIndex={lightboxState.index}
          onClose={() => setLightboxState({ isOpen: false, images: [], index: 0 })}
        />
      )}
    </div>
  )
}
